import React, { useEffect, useRef, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_DEPRESSION,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";

import {
  calculateTotalScoreDepression,
  getDepressionLevel,
} from "../../utils/assessment-utils";

import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import { useTranslation } from "react-i18next";
import {
  getEnneagramBannerImage,
  getDepressionImage,
} from "../../utils/utility-functions";
import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";
import Footer from "../../components/footer";
import ExpandableSection from "../../components/expandable-collapsible/component";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import { CallToActionSection } from "../../components/call-to-action/component";
import InviteFriend from "../../components/invite-friend";
import PathHelper from "../../utils/path-helper";

const DepressionReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,

  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,
  getGuestUserConnect,
  getTransactionDetailConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) => {
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  const assessmentId = currentAssessmentAttemptData?.assessmentId;
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const { t } = useTranslation();
  const depression1 = "depression-result-1";
  const depression2 = "depression-result-2";
  const depression3 = "depression-result-3";
  const depressionImage1 = getDepressionImage(depression1);
  const depressionImage2 = getDepressionImage(depression2);
  const depressionImage3 = getDepressionImage(depression3);
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_DEPRESSION[currentLocale] ||
            MODEL_TYPE_DEPRESSION[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;

          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId, currentLocale]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  const bannerImage = getEnneagramBannerImage();
  const disclaimer = contentData?.disclaimer;

  // Get highest score from individual traits
  const getHighestScore = (traits) => {
    if (!Array.isArray(traits) || traits.length === 0) return 0;
    return Math.max(...traits.map((trait) => trait?.score || 0));
  };

  // Map highest individual score to severity level for section-based results
  const getSeverityLevel = (highestScore) => {
    if (highestScore <= 19) {
      return "minimal";
    } else if (highestScore <= 29) {
      return "mild";
    } else if (highestScore <= 39) {
      return "moderate";
    } else {
      return "severe";
    }
  };

  // Get overall results content based on depression level
  const getOverallResultsContent = () => {
    const totalScore = calculateTotalScoreDepression(userTraits);
    const depressionLevel = getDepressionLevel(totalScore);

    if (!depressionLevel || !contentData?.overall_results) return null;

    return contentData.overall_results[depressionLevel];
  };

  const overallResultsContent = getOverallResultsContent();

  // Get background color based on depression level
  const getDepressionLevelBgColor = () => {
    const totalScore = calculateTotalScoreDepression(userTraits);
    const depressionLevel = getDepressionLevel(totalScore);

    switch (depressionLevel) {
      case "minimal":
        return "bg-green-200"; // Not depressed - green
      case "mild":
        return "bg-yellow-200"; // Mild - yellow
      case "moderate":
        return "bg-orange-200"; // Moderate - orange
      case "severe":
        return "bg-red-200"; // Severe - red
      case "very_severe":
        return "bg-red-300"; // Very severe - darker red
    }
  };

  const dynamicBgColor = getDepressionLevelBgColor();

  // Get severity level for section-based results
  const highestScore = getHighestScore(userTraits);
  const severityLevel = getSeverityLevel(highestScore);

  // Helper function to get individual trait score by trait.title (snake_case)
  const getTraitScore = (traitTitle) => {
    const trait = userTraits.find((t) => t.trait?.title === traitTitle);
    return trait?.score || 0;
  };

  // Calculate individual severity levels for each section
  const physicalBehavioralScore = getTraitScore(
    "physical_and_behavioral_changes"
  );
  const physicalBehavioralSeverity = getSeverityLevel(physicalBehavioralScore);

  const moodEmotionalScore = getTraitScore("mood_and_emotional_state");
  const moodEmotionalSeverity = getSeverityLevel(moodEmotionalScore);

  const energyMotivationScore = getTraitScore("energy_and_motivation");
  const energyMotivationSeverity = getSeverityLevel(energyMotivationScore);

  const thoughtsCognitionScore = getTraitScore("thoughts_and_cognition");
  const thoughtsCognitionSeverity = getSeverityLevel(thoughtsCognitionScore);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };
  const HeroBanner = ({ title, color = "purple" }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    if (!title) return null;
    const isString = typeof title === "string";
    let firstWord = "";
    let restOfTitle = "";
    if (isString) {
      const words = title.split(" ");
      firstWord = words[0];
      restOfTitle = words.slice(1).join(" ");
    }

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        <div
          className={`absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent z-5`}
        />
        <div className="absolute inset-0 bg-black opacity-10 z-5" />

        <div className="relative h-full flex items-start" id={reportType}>
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12" id={reportType}>
            <div
              className="flex flex-col md:flex-row justify-between gap-4 items-start pr-10 md:pr-16"
              id={reportType}
            >
              <h2
                id={reportType}
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl font-bold text-white text-left break-words whitespace-normal"
              >
                {isString ? (
                  <span className="inline-block max-w-full md:max-w-none leading-tight">
                    <span className="block sm:inline">{firstWord}</span>
                    {restOfTitle && (
                      <>
                        <span className="hidden sm:inline"> </span>
                        <span className="block sm:inline">{restOfTitle}</span>
                      </>
                    )}
                  </span>
                ) : (
                  title
                )}
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const CallToActionSectionForUnAuth = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.colorblindness.personality_test_cta")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.enneagram.discover_more_about_your_personality_type")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              id="depression-report-take-personality-test-btn"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("dashboard.take_test_cta")}
            </button>
          </div>
        </div>
      </section>
    );
  };
  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
    dualSectionData, // 👈 new optional prop
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} mb-8 flex flex-col h-full`}
      >
        {/* --- Main Heading --- */}
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]"></div>
                )}
                <h2
                  className={`text-lg sm:text-xl ${textClr} font-semibold flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {/* --- Description --- */}
        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 break-words overflow-wrap-anywhere ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray || dualSectionData ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}

        {/* --- NEW: Dual section layout --- */}
        {dualSectionData && (
          <div className={`space-y-6 mt-4`}>
            {Object.values(dualSectionData)
              .filter((section) => section)
              .map((section, idx) => (
                <div key={idx}>
                  <h3
                    className={`text-lg font-semibold mb-1 ${
                      textClr || "text-gray-800"
                    }`}
                  >
                    {section?.heading}
                  </h3>
                  <p className="text-gray-700 text-base leading-relaxed mb-2 break-words overflow-wrap-anywhere">
                    <strong>{t("report.depression.meaning")}</strong>{" "}
                    {section?.meaning}
                  </p>
                  <p className="text-gray-700 text-base leading-relaxed break-words overflow-wrap-anywhere">
                    <strong>{t("report.depression.recommendation")}</strong>{" "}
                    {section?.recommendation}
                  </p>
                </div>
              ))}
          </div>
        )}

        {/* --- Existing list rendering (unchanged) --- */}
        {contentArray && !dualSectionData && (
          <ul
            className={`space-y-2 mt-2 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => (
              <li key={index} className="flex gap-4">
                <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]"></div>
                <div className="flex flex-col">
                  {isSubHeading ? (
                    <>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold text-gray-700">
                          {item?.heading}
                        </h2>
                      </div>
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed relative break-words overflow-wrap-anywhere">
                        <span>{item.content}</span>
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed break-words overflow-wrap-anywhere">
                      {item}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const InfoListCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    className = "",
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className={`p-4 md:p-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full ${className}`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 break-words overflow-wrap-anywhere ${
              contentArray ? `mb-4` : ""
            }`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-2 mt-4 pl-6 md:pl-10">
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative break-words overflow-wrap-anywhere">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed break-words overflow-wrap-anywhere">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };
  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
        style={{ filter: "drop-shadow(0 0 5px rgba(0,0,0,0.3))" }}
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          stroke="none"
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.heading} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[65%_35%] gap-4 items-start">
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        <h2 className="text-3xl font-semibold text-gray-700 mt-1 pb-4 break-words whitespace-normal">
                          {t("report.depression.description")}
                        </h2>
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                          {contentData.description}
                        </p>
                      </div>
                      <div className="flex flex-col items-center justify-start gap-4 md:gap-10">
                        <img
                          src={depressionImage1}
                          alt="Banner Image"
                          className="w-full max-w-md mx-auto object-contain mt-10 "
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.["overall_results"]?.heading}
                      color="blue"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                      {/* Top-left */}
                      <DescriptiveCard
                        heading={overallResultsContent?.heading}
                        overview={overallResultsContent?.flavorText}
                        bgClr={dynamicBgColor}
                      />
                      {/* Top-right */}
                      <InfoListCard
                        character={"i"}
                        heading={overallResultsContent?.recommendation_heading}
                        overview=""
                        contentArray={
                          overallResultsContent?.recommendation || []
                        }
                      />
                      {/* Bottom-left */}
                      <InfoListCard
                        character={"i"}
                        heading={overallResultsContent?.meaning_heading}
                        overview=""
                        contentArray={overallResultsContent?.meaning || []}
                      />
                      <div className="flex flex-col items-center md:justify-center space-y-6">
                        {/* Image */}
                        <img
                          src={depressionImage2}
                          alt="Banner Image"
                          className="w-full max-w-md mx-auto object-contain mt-10"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.["section-based"]?.heading}
                      color="blue"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10 items-stretch">
                      {/* Section-Based Result */}

                      <div className="flex flex-col h-full">
                        {/* Heading and Overview OUTSIDE the card */}
                        <h2 className="text-xl font-semibold text-gray-700 mb-2">
                          {
                            contentData?.["section-based"]
                              ?.physicalAndBehavioralChanges?.heading
                          }
                        </h2>
                        <p className="text-gray-700 mb-4 break-words overflow-wrap-anywhere">
                          {
                            contentData?.["section-based"]
                              ?.physicalAndBehavioralChanges?.description
                          }
                        </p>

                        {/* DescriptiveCard only for inner details */}
                        <div className="flex-1">
                          <DescriptiveCard
                            bgClr="bg-red-50"
                            textClr="text-red-800"
                            dualSectionData={{
                              [physicalBehavioralSeverity]:
                                contentData?.["section-based"]
                                  ?.physicalAndBehavioralChanges?.[
                                  physicalBehavioralSeverity
                                ],
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col h-full">
                        {/* Heading and Overview OUTSIDE the card */}
                        <h2 className="text-xl font-semibold text-gray-700 mb-2">
                          {
                            contentData?.["section-based"]
                              ?.moodAndEmotionalState?.heading
                          }
                        </h2>
                        <p className="text-gray-700 mb-4 break-words overflow-wrap-anywhere">
                          {
                            contentData?.["section-based"]
                              ?.moodAndEmotionalState?.description
                          }
                        </p>
                        <div className="flex-1">
                          <DescriptiveCard
                            bgClr="bg-sky-100"
                            textClr="text-sky-800"
                            dualSectionData={{
                              [moodEmotionalSeverity]:
                                contentData?.["section-based"]
                                  ?.moodAndEmotionalState?.[
                                  moodEmotionalSeverity
                                ],
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col h-full">
                        {/* Heading and Overview OUTSIDE the card */}
                        <h2 className="text-xl font-semibold text-gray-700 mb-2">
                          {
                            contentData?.["section-based"]?.energyAndMotivation
                              ?.heading
                          }
                        </h2>
                        <p className="text-gray-700 mb-4 break-words overflow-wrap-anywhere">
                          {
                            contentData?.["section-based"]?.energyAndMotivation
                              ?.description
                          }
                        </p>
                        <div className="flex-1">
                          <DescriptiveCard
                            bgClr="bg-emerald-50"
                            textClr="text-emerald-800"
                            dualSectionData={{
                              [energyMotivationSeverity]:
                                contentData?.["section-based"]
                                  ?.energyAndMotivation?.[
                                  energyMotivationSeverity
                                ],
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col h-full">
                        {/* Heading and Overview OUTSIDE the card */}
                        <h2 className="text-xl font-semibold text-gray-700 mb-2">
                          {
                            contentData?.["section-based"]?.thoughtsAndCognition
                              ?.heading
                          }
                        </h2>
                        <p className="text-gray-700 mb-4 break-words overflow-wrap-anywhere">
                          {
                            contentData?.["section-based"]?.thoughtsAndCognition
                              ?.description
                          }
                        </p>
                        <div className="flex-1">
                          <DescriptiveCard
                            bgClr="bg-violet-50"
                            textClr="text-violet-800"
                            dualSectionData={{
                              [thoughtsCognitionSeverity]:
                                contentData?.["section-based"]
                                  ?.thoughtsAndCognition?.[
                                  thoughtsCognitionSeverity
                                ],
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.understandingDepression?.heading}
                      color="blue"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6 space-y-6">
                    {/* Common Symptoms Section */}
                    <div>
                      <InfoListCard
                        heading={
                          contentData?.understandingDepression?.symptoms
                            ?.heading
                        }
                        overview={
                          contentData?.understandingDepression?.symptoms
                            ?.flavorText
                        }
                        contentArray={
                          contentData?.understandingDepression?.symptoms
                            ?.content
                        }
                        bgClr="#ffe0e0"
                        character="i"
                      />
                    </div>

                    {/* Impact on Quality of Life */}
                    <div>
                      <InfoListCard
                        heading={
                          contentData?.understandingDepression
                            ?.impactOnQualityOfLife?.heading
                        }
                        overview={
                          contentData?.understandingDepression
                            ?.impactOnQualityOfLife?.flavorText
                        }
                        contentArray={
                          contentData?.understandingDepression
                            ?.impactOnQualityOfLife?.content
                        }
                        bgClr="#e0f7fa"
                        character="i"
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={
                        contentData?.understandingDepression
                          ?.whyEarlyRecognitionMatters?.heading
                      }
                      color="blue"
                    />
                  }
                >
                  <div className="grid grid-cols-1 lg:grid-cols-[65%_35%] gap-4 items-start">
                    <div className="w-full p-4 md:px-8 md:py-6">
                      <p
                        className={`"text-gray-700 text-base md:text-lg leading-relaxed mb-4"`}
                      >
                        {
                          contentData?.understandingDepression
                            ?.whyEarlyRecognitionMatters?.content
                        }
                      </p>
                    </div>
                    <div className="flex flex-col items-center justify-start gap-4 md:gap-10 mb-5">
                      <img
                        src={depressionImage3}
                        alt="Banner Image"
                        className="w-full max-w-md object-contain"
                      />
                    </div>
                  </div>
                  <DescriptiveCard
                    heading={disclaimer?.heading}
                    overview={disclaimer?.content}
                    bgClr={"bg-orange-50"}
                    textClr={"text-emerald-800"}
                  />
                </ExpandableSection>
              </section>
              {isAuth ? (
                <CallToActionSection
                  currentLocale={currentLocale}
                  t={t}
                  getAssessmentConnect={getAssessmentConnect}
                  getAssessmentAttemptByUserIdConnect={
                    getAssessmentAttemptByUserIdConnect
                  }
                  assessmentId={assessmentId}
                  userId={userGUID}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.BLUE_INDIGO}
                />
              ) : (
                <CallToActionSectionForUnAuth data={"contentData"} />
              )}

              {isAuth && (
                <InviteFriend
                  sectionMode={true}
                  getGuestUserConnect={getGuestUserConnect}
                  getTransactionDetailConnect={getTransactionDetailConnect}
                  getBillingConnect={getBillingConnect}
                  guestUserChangesConnect={guestUserChangesConnect}
                  isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                  userId={userGUID}
                  userEmail={userData?.email}
                  buttonText={t("dashboard.invite_friend_cta")}
                  selectedPlan={selectedPlan}
                  assessmentData={currentAssessmentAttemptData}
                  showRemainingInvites={true}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                />
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DepressionReport;
